import React, { useMemo } from 'react';
import { useMemoizedFn } from 'ahooks';
import ReportWallpaper from '../../../components/ReportWallpaper';
import Comment from '../Comment';
import Hightlights from '../Hightlights';
import Changes from '../Changes';
import Miscellaneous from '../Miscellaneous';
import Calendar from '../Calendar';
import { getDPSReportDateTitle } from '../../../utils';

import type { IBaseProps } from '@home/types/common';
import type { IDPSReportHelperVO, IDPSReportCalendarVO } from '@gp/types/dpsReport/common';
import type {
  IDPSReportHightlightVO,
  IDPSReportChangeVO,
  IDPSReportOtherOrMiscellaneousVO,
  IDPSReportProjectsMilestonesVOV2,
} from '@gp/types/dpsReport/v2';
import type { IDPSReportFormValue } from '../../types';

interface IReportFormProps extends IBaseProps {
  // 是否loading
  isLoading?: boolean;
  isHightlightsInnerLoading?: boolean;
  isCalendarInnerLoading?: boolean;
  reportHelper?: IDPSReportHelperVO | null;
  projectsMilestones?: IDPSReportProjectsMilestonesVOV2;
  // hightlights模块点击Refresh Data按钮的回调
  onHightlightsRefreshData?: () => void;
  // calendar模块点击Refresh Data按钮的回调
  onCalendarRefreshData?: () => void;
  value: IDPSReportFormValue;
  onChange?: (payload: IDPSReportFormValue) => void;
}

const ReportForm: React.FC<IReportFormProps> = (props) => {
  const {
    isLoading,
    isHightlightsInnerLoading,
    isCalendarInnerLoading,
    reportHelper,
    projectsMilestones,
    onHightlightsRefreshData,
    onCalendarRefreshData,
    value,
    onChange,
  } = props;

  const dateTitle = useMemo<string>(() => getDPSReportDateTitle(reportHelper), [reportHelper]);

  const handleCommentChange = useMemoizedFn((payload: string) => {
    const valueTmp = { ...value, comment: payload };
    onChange?.(valueTmp);
  });

  const handleHightlightsChange = useMemoizedFn((payload: IDPSReportHightlightVO[]) => {
    const valueTmp = { ...value, hightlights: payload };
    onChange?.(valueTmp);
  });

  const handleChangesChange = useMemoizedFn((payload: IDPSReportChangeVO[]) => {
    const valueTmp = { ...value, changes: payload };
    onChange?.(valueTmp);
  });

  const handleMiscellaneousChange = useMemoizedFn((payload: IDPSReportOtherOrMiscellaneousVO[]) => {
    const valueTmp = { ...value, miscellaneous: payload };
    onChange?.(valueTmp);
  });

  const handleCalendarChange = useMemoizedFn((payload: IDPSReportCalendarVO[]) => {
    const valueTmp = { ...value, calendar: payload };
    onChange?.(valueTmp);
  });

  return (
    <ReportWallpaper dateTitle={dateTitle}>
      <Comment isLoading={isLoading} value={value?.comment} onChange={handleCommentChange} />
      <Hightlights
        isLoading={isLoading}
        isInnerLoading={isHightlightsInnerLoading}
        projectsMilestones={projectsMilestones?.hightlights}
        value={value?.hightlights}
        onChange={handleHightlightsChange}
        onRefreshData={onHightlightsRefreshData}
      />
      <Changes
        isLoading={isLoading}
        projectsMilestones={projectsMilestones?.changes}
        value={value?.changes}
        onChange={handleChangesChange}
      />
      <Miscellaneous
        isLoading={isLoading}
        projectsMilestones={projectsMilestones?.miscellaneous}
        value={value?.miscellaneous}
        onChange={handleMiscellaneousChange}
      />
      <Calendar
        isLoading={isLoading}
        isInnerLoading={isCalendarInnerLoading}
        projectsMilestones={projectsMilestones?.calendar}
        value={value?.calendar}
        onChange={handleCalendarChange}
        onRefreshData={onCalendarRefreshData}
      />
    </ReportWallpaper>
  );
};

export default ReportForm;
